import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, BriefcaseBusiness, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Gate } from '../components/shell/gate.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { Input } from '../components/ui/input.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { describeError } from '../lib/api.ts';
import { api, type ProjectRoleView } from '../lib/client.ts';
import { projectRolesQuery } from '../lib/record-data.ts';
import { useSession } from '../lib/session.tsx';

/**
 * Settings → Projects: the functions a project can have someone in (Account Manager, Sales Rep…),
 * beside its owner. Whoever holds one on a project can edit it as its owner can, sees it under
 * "Mine", and can be the assignee of an automation. Everyone may look; changing the list needs
 * `organisation.manage`. Removing a function takes it off every project that had someone in it.
 */
export function ProjectSettingsPage() {
  const session = useSession();
  const qc = useQueryClient();
  const key = projectRolesQuery(session.me.organisation.id);
  const canManage = session.can('organisation.manage');
  const roles = useQuery({ queryKey: key, queryFn: api.projectRoles.list });
  const [naming, setNaming] = useState<ProjectRoleView | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ProjectRoleView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const write = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: key });
    },
    onError: (err) => setError(describeError(err)),
  });
  const close = () => {
    setError(null);
    setNaming(null);
    setDeleting(null);
  };
  const list = roles.data ?? [];
  const move = (index: number, by: -1 | 1) => {
    const ids = list.map((r) => r.id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + by, 0, id!);
    write.mutate(() => api.projectRoles.reorder(ids));
  };
  const add = canManage && (
    <Button onClick={() => setNaming('new')} data-testid="new-project-role">
      <Plus /> Add function
    </Button>
  );

  return (
    <Gate permission="organisation.read">
      <PageHeader
        title="Project settings"
        description="Who answers for a project, beside its owner."
        actions={add}
      />
      {error && naming === null && deleting === null && (
        <p className="text-danger mb-4 text-sm" role="alert">
          {error}
        </p>
      )}
      {roles.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(roles.error)}
        </p>
      )}
      {roles.isPending && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {roles.data &&
        (list.length === 0 ? (
          <EmptyState
            icon={BriefcaseBusiness}
            title="No project functions yet."
            description="Add the functions someone holds on a project, such as Account Manager or Sales Rep."
            action={add}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Responsible functions</CardTitle>
              <CardDescription>
                Each project has at most one person per function. They can edit the project as its
                owner can, see it under Mine, and be assigned tasks by automations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-border divide-y" aria-label="Project functions">
                {list.map((role, i) => (
                  <li
                    key={role.id}
                    className="flex items-center justify-between gap-3 py-2"
                    data-testid="project-role"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium">{role.name}</p>
                      <p className="text-muted-foreground text-xs">
                        <code title="Its permanent key for integrations">{role.slug}</code> ·{' '}
                        {role.assignedCount} project{role.assignedCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    {canManage && (
                      <div className="flex shrink-0 gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={i === 0 || write.isPending}
                          onClick={() => move(i, -1)}
                          aria-label={`Move ${role.name} up`}
                        >
                          <ArrowUp />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={i === list.length - 1 || write.isPending}
                          onClick={() => move(i, 1)}
                          aria-label={`Move ${role.name} down`}
                        >
                          <ArrowDown />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setNaming(role)}
                          aria-label={`Rename ${role.name}`}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-danger hover:text-danger"
                          onClick={() => setDeleting(role)}
                          aria-label={`Remove ${role.name}`}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}

      <NameDialog
        key={naming === 'new' ? 'new' : (naming?.id ?? 'closed')}
        role={naming}
        busy={write.isPending}
        error={error}
        onClose={close}
        onSave={(name) =>
          write.mutate(
            () =>
              naming === 'new'
                ? api.projectRoles.create(name)
                : api.projectRoles.rename(naming!.id, name),
            { onSuccess: () => setNaming(null) },
          )
        }
      />

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {deleting?.name}?</DialogTitle>
            <DialogDescription>
              {deleting?.assignedCount
                ? `It is taken off the ${deleting.assignedCount} project${deleting.assignedCount === 1 ? '' : 's'} that have someone in it, and those people lose the access it gave them.`
                : 'No project has anyone in it.'}
            </DialogDescription>
          </DialogHeader>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={write.isPending}
              data-testid="confirm-remove-project-role"
              onClick={() =>
                deleting &&
                write.mutate(() => api.projectRoles.remove(deleting.id), {
                  onSuccess: () => setDeleting(null),
                })
              }
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Gate>
  );
}

function NameDialog({
  role,
  busy,
  error,
  onClose,
  onSave,
}: {
  role: ProjectRoleView | 'new' | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(role && role !== 'new' ? role.name : '');
  const trimmed = name.trim();
  return (
    <Dialog open={role !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {role === 'new' ? 'Add a function' : `Rename ${role?.name ?? ''}`}
          </DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed) onSave(trimmed);
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Name
            <Input
              autoFocus
              value={name}
              maxLength={80}
              placeholder="Account Manager"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed || busy}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
